<template>
  <div
    id="app-navigation"
    class="flex items-center justify-center md:justify-between
      py-3 md:p-4 md:px-6 max-md:mb-4 sticky md:relative top-0 z-40 bg-inherit max-md:bg-white"
    >
    <div class="app-max-width flex items-center justify-between mx-[3vw] !w-full md:m-auto">
      <div class="flex items-center gap-6">
        <ui-link class="flex items-center gap-2 !no-underline" :to="isAuthed ? '/home' : '/'">
          <ui-server-img
            src="logo.webp"
            class="rounded-md h-8"
            :height="32"
            :rounding="false"
            alt="Learn ATC Logo showing a blue background and white 2D airplane" />

          <span class="block xl:hidden text-xl text-[#111724] font-bold truncate"><span class="text-gray-400/80">L<span class="text-xs align-middle mb-1 inline-block">-</span></span>ATC</span>
          <span class="hidden xl:block text-xl text-[#111724] font-bold truncate">Learn ATC</span>
        </ui-link>
        <ui-language-switcher />
        <div class="hidden md:flex gap-4 lg:gap-6 font-semibold">
          <ui-navigation-link
            v-for="item in computedDesktopMenu"
            :key="item.text"
            :item="item"
            />
        </div>
      </div>
      <div class="flex items-center gap-2">
        <ui-link
          v-if="!isAuthed"
          class="hidden md:block font-semibold text-[#111724] bg-white p-2 px-3 lg:px-3 content-center rounded-lg"
          to="/login"
          :text="t('Login')"
          />
        <ui-link
          v-if="!isAuthed"
          class="hidden md:block font-semibold text-white bg-theme p-2 px-3 content-center rounded-lg dark"
          to="/register"
          :text="t('Register')"
          />
        <ui-navigation-profile-dropper
          v-if="isAuthed"
          class="hidden md:inline-flex"
          />
        <span class="ml-2 md:hidden" @click="dataShowMobileFlyout = !dataShowMobileFlyout">
          <i class="cursor-pointer fas fa-bars text-[1.6rem] pt-1" />
        </span>
      </div>
    </div>

    <!-- ================== Mobile -->
    <div
      v-if="dataShowMobileFlyout"
      class="fixed md:hidden left-0 right-0 bottom-0 top-[52px] bg-white z-50 select-none max-w-[100vw]">
      <div class="mobile-menu">
        <ui-text-label text="MENU" />
        <ui-dropper
          v-show="computedOpenIndex ? i === Number(computedOpenIndex) : true"
          v-for="(item,i) in computedMobileMenu"
          :key="item.text"
          v-model="dataMobileOpen[i]"
          :start-open="false"
          :prevent-open="!item?.children?.length"
          :head-classes="{
            'border-gray-100': Number(computedOpenIndex) !== i,
            'border-transparent': i === computedMobileMenu.length - 1
          }"
          >
          <template #head>
            <ui-link
              :to="{path: item?.to}"
              :class="[
                'mobile-menu-item w-full flex items-center justify-between py-1',
                i === computedMobileMenu.length - 1 ? '!border-b-0' : '',
              ]"
              title=""
              @click="dataShowMobileFlyout = false"
              >
              <span v-text="t(item.text)" />
            </ui-link>
            <span class="text-[12px]">
              <i v-if="!item?.children?.length" class="fas fa-share text-xs pr-2 text-gray-300 !no-underline" />
              <i v-else-if="!dataMobileOpen[i]" class="w-16 text-center border-2 border-primary text-primary ui-rounded p-2 flex-grow fas fa-chevron-right !no-underline" @click.capture.prevent.prevent.prevent />
              <i v-else class="w-16 text-center border-2 border-primary text-primary ui-rounded p-2 flex-grow fas fa-chevron-down !no-underline" @click.capture.prevent.prevent.prevent />
            </span>
          </template>
          <div class="p-2 flex flex-col">
            <ui-link
              v-for="child in item.children?.filter((x:any) => x.auth === null || x.auth === isAuthed)"
              :key="child.text"
              class="mobile-menu-item w-full flex items-center justify-between py-2 border-b"
              :to="{path: child?.to}"
              @click="dataShowMobileFlyout = false"
              >
              <span v-text="t(child.text)" />
              <i class="fas fa-share text-xs pr-2 text-gray-300 !no-underline" />
            </ui-link>
            <div class="flex-grow"/>
            <div class="pt-4">
              <ui-button
                class="ui-button gray p-4 font-bold text-sm text-white cursor-pointer w-full"
                :text="t('Back')"
                @click="methodUndropAll"
                />
            </div>
          </div>
        </ui-dropper>

        <div v-if="isAuthed" class="pt-4 w-full">
          <ui-button
            class="ui-button p-4 font-bold text-sm bg-red-900/60 text-white cursor-pointer w-full"
            :text="t('Logout')"
            @click="logout"
            />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const route = useRoute();
const dataShowMobileFlyout = ref(false)
const dataMobileOpen = ref<Record<any,boolean>>({})

const { t } = useI18n()

const desktopMenu = ref([
  { auth: false, text: ('Features'), to: '/features',
    children: [
      { auth: null, text: ('Simulator'), to: '/features#simulator', svg: 'fa fa-plane' },
      { auth: null, text: ('Questions'), to: '/features#questions', svg: 'fa fa-question-circle' },
      { auth: null, text: ('Spelling'), to: '/features#spelling', svg: 'fa fa-spell-check' },
      { auth: null, text: ('Listening'), to: '/features#listening', svg: 'fa fa-headphones' },
      { auth: null, text: ('Scenarios'), to: '/features#scenarios', svg: 'fa fa-file-alt' },
      { auth: null, text: ('Wiki'), to: '/features#wiki', svg: 'fa fa-book' },
    ]
  },
  { auth: true, text: ('School'), to: '/home/school',
    children: [
      { auth: null, text: ('Syllabus'), to: '/home/school/syllabus', svg: 'fas fa-book' },
      { auth: null, text: ('Simulator'), to: '/home/school/simulator', svg: 'fas fa-plane' },
      { auth: null, text: ('Questions'), to: '/home/school/questions', svg: 'fas fa-question-circle',
        children: [
          { auth: null, text: ('BPLG'), to: '/home/school/questions/bplg', svg: 'fas fa-book' },
          { auth: null, text: ('BPLH'), to: '/home/school/questions/bplh', svg: 'fas fa-book' },
          { auth: null, text: ('PPL (A)'), to: '/home/school/questions/ppl(a)', svg: 'fas fa-book' },
          { auth: null, text: ('PPL (H)'), to: '/home/school/questions/ppl(h)', svg: 'fas fa-book' },
          { auth: null, text: ('PU'), to: '/home/school/questions/pu', svg: 'fas fa-book' },
          { auth: null, text: ('SPL'), to: '/home/school/questions/spl', svg: 'fas fa-book' },
        ]
      },
      { auth: null, text: ('Lections'), to: '/home/school/lections', svg: 'fas fa-book-open' },
      { auth: null, text: ('Scenarios'), to: '/home/school/scenarios', svg: 'fas fa-file-alt' },
    ]
  },
  {
    auth:null, text: ('Tools'), to: '/tools',
    children: [
      { auth: null, text: ('Flightschools'), to: '/tools/flightschool', svg: 'fas fa-school' },
      { auth: null, text: ('Aerodromes'), to: '/tools/aerodrome', svg: 'fas fa-landmark' },
      { auth: null, text: ('Frequencies'), to: '/tools/frequency', svg: 'fas fa-broadcast-tower' },
      { auth: null, text: ('Blog'), to: '/blog', svg: 'fas fa-blog', },
      { auth: null, text: ('Wiki'), to: '/wiki', svg: 'fas fa-book', },
    ]
  },
  {
    auth:null, text: ('Other'),
    children: [
      { auth: true, text: ('Pricing'), to: '/pricing', svg: 'fas fa-tags' },
      { auth: false, text: ('B2B'), to: '/b2b', svg: 'fas fa-handshake' },
      { auth: null, text: ('FAQ'), to: '/faq', svg: 'fas fa-question-circle' },
      { auth: null, text: ('tos'), to: '/tos', svg: 'fas fa-file-contract' },
      { auth: null, text: ('Privacy'), to: '/privacy', svg: 'fas fa-user-shield' },
    ]
  },
  { auth: false, text: ('Pricing'), to: '/pricing' },
  { auth: false, text: ('Login'), to: '/login', mobile: true},
  { auth: false, text: ('Register'), to: '/register', mobile: true},
])

const methodUndropAll = () => {
  Object.keys(dataMobileOpen.value).forEach((key) => {
    dataMobileOpen.value[key] = false
  })
}

const computedOpenIndex = computed(() => {
  return Object.keys(dataMobileOpen.value).filter(key => dataMobileOpen.value[key] === true)?.[0]
})

const computedMobileMenu = computed(() => {
  return desktopMenu.value.filter((item) => item.auth === null || item.auth === isAuthed.value)
})

const computedDesktopMenu = computed(() => {
  return desktopMenu.value.filter((item) => (item.auth === null || item.auth === isAuthed.value) && !item.mobile)
})

watch(route, () => {
  dataShowMobileFlyout.value = false
  methodUndropAll()
})
</script>

<style scoped lang="scss">
.mobile-menu {
	@apply flex flex-wrap text-xl font-bold h-min m-4 py-4 pb-16 bg-white justify-start items-start;

	// & > .mobile-menu-item {
	// 	@apply flex justify-between bg-white mx-4 p-3 border-b border-gray-100 text-lg font-semibold hover:bg-gray-100;
  //   @apply transition-all duration-300 ease-out no-underline text-slate-800 font-semibold items-center;
	// }
}
</style>
